/**
 * `LagHistoryBar` — second bar series rendered under each `(freshness)` row
 * in `IndexerPipelineCard`. Shares the same cell-grid sizing as `UptimeBlock`
 * (`flex h-9 items-stretch gap-[2px]` + `min-w-[2px] flex-1` cells) so the
 * two stacked bars align column-for-column.
 *
 * Visual encoding (per DESIGN.md §6.1 carve-out for freshness magnitude bars):
 *   - Bar HEIGHT = log-scaled `observedLagSec` p95 (via `lagToHeightPct`)
 *   - Bar COLOR  = severity per pipeline thresholds (via `lagToTone`)
 *   - `null` bucket (no samples in this slot) → full-height neutral grey to
 *     visually distinguish from "0 lag" (which renders at the floor).
 */
import clsx from 'clsx';
import { memo, useState } from 'react';
import {
  computeDynamicClampSec,
  lagToHeightPct,
  lagToTone,
  type LagTone,
  type Pipeline,
} from '../lib/lag-bars';
import { padTrailingSlots } from '../lib/lag-pad';
import { formatLagTimeRange } from '../lib/lag-time';
import {
  HoverTooltip,
  TipDetail,
  TipHeader,
  TipTime,
} from './HoverTooltip';

export interface LagBucketView {
  startAt: string;
  endAt: string;
  lagP95Sec: number | null;
  lagMaxSec: number | null;
  /**
   * Block-gap stats — only set for buckets containing `(freshness)` probes
   * that target a sequence-numbered table. Undefined or `null` for buckets
   * with no measurable block-gap (e.g. masterchain freshness probe alone).
   */
  lagP95Blocks?: number | null;
  lagMaxBlocks?: number | null;
  sampleCount: number;
  /**
   * Count of probes in this bucket with a measured block-gap. Retained from
   * the API payload but no longer surfaced in the UI — lag is reported in
   * seconds only.
   */
  sampleCountBlocks?: number;
  /**
   * Per-status probe counts within this bucket (per endpoint). Used to
   * paint the bar red on failures even when no lag was measurable (e.g.
   * upstream returned 503 — probes never reached the freshness extract
   * path, so `sampleCount` is 0 but `downCount > 0`).
   */
  okCount?: number;
  warnCount?: number;
  downCount?: number;
  totalProbes?: number;
}

export type WindowSel = '90d' | '24h' | '1h';

export interface LagHistoryBarProps {
  buckets: readonly LagBucketView[];
  pipeline: Pipeline;
  windowSel: WindowSel;
  expectedCount: number;
  /** Endpoint key used for the accessibility label only. */
  endpointKey: string;
  /**
   * Tailwind height class for the bar row. Defaults to `h-9` (the inline
   * sparkline height used on the page). The detail modal passes a taller
   * class (e.g. `h-40`) to render the same series at full size.
   */
  heightClass?: string;
}

const TONE_BAR: Record<LagTone, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  down: 'bg-down',
  none: 'bg-[rgb(var(--bar-empty))]',
};

/**
 * Compact lag formatter for the screen-reader summary only. Kept local
 * (rather than importing `formatLag` from `IndexerPipelineCard`) because
 * `IndexerPipelineCard` already imports this module — pulling `formatLag`
 * back in would create a circular import. Mirrors its "—" for no data and
 * "Ns" / "Mm Ss" shape closely enough for the aria-label headline.
 */
function formatLagForLabel(seconds: number | null): string {
  if (seconds === null) return 'unknown';
  if (seconds < 60) {
    const r = Math.round(seconds * 10) / 10;
    return r <= 0 ? 'under 0.1s' : `${String(r)}s`;
  }
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return s === 0 ? `${String(m)}m` : `${String(m)}m ${String(s)}s`;
}

/**
 * Aggregate headline so non-visual (screen-reader / no-pointer) users get
 * the chart's signal — peak p95 lag plus how many buckets went down/warn —
 * without the mouse-only HoverTooltip. Plain sentence-case prose per
 * DESIGN.md §11/§14 ("colour is never the only encoding").
 */
function buildAriaSummary(
  windowSel: WindowSel,
  endpointKey: string,
  buckets: readonly LagBucketView[],
): string {
  let peakP95: number | null = null;
  let downBuckets = 0;
  let warnBuckets = 0;
  for (const b of buckets) {
    if (b.lagP95Sec !== null && (peakP95 === null || b.lagP95Sec > peakP95)) {
      peakP95 = b.lagP95Sec;
    }
    if ((b.downCount ?? 0) > 0) downBuckets += 1;
    if ((b.warnCount ?? 0) > 0) warnBuckets += 1;
  }
  return (
    `${windowSel} lag history for ${endpointKey}: ` +
    `peak p95 ${formatLagForLabel(peakP95)}, ` +
    `${String(downBuckets)} down, ${String(warnBuckets)} warn`
  );
}

function LagHistoryBarImpl({
  buckets,
  pipeline,
  windowSel,
  expectedCount,
  endpointKey,
  heightClass = 'h-9',
}: LagHistoryBarProps) {
  const windowMs =
    windowSel === '1h' ? 60_000 : windowSel === '24h' ? 15 * 60_000 : 86_400_000;
  const renderNow = Date.now();

  // Pad with placeholders so the row width matches the uptime bar above.
  // Two phases (fill-to-`expectedCount` + extend up-to-3 in-flight slots,
  // anchored-past guarded) live in `padTrailingSlots` — kept in lockstep with
  // App.tsx's `padTrailingPending` via that one tested helper. `renderNow` is
  // captured once per render above; recomputed every render so the in-flight
  // slot follows the clock (unchanged from the prior inline implementation).
  const lastReal = buckets.length > 0 ? buckets[buckets.length - 1] : null;
  const lastEndAt = lastReal ? lastReal.endAt : undefined;
  const padded = padTrailingSlots<LagBucketView>(
    buckets,
    expectedCount,
    windowMs,
    renderNow,
    lastEndAt,
    (start, end) => ({
      startAt: new Date(start).toISOString(),
      endAt: new Date(end).toISOString(),
      lagP95Sec: null,
      lagMaxSec: null,
      sampleCount: 0,
    }),
    () => ({
      startAt: '',
      endAt: '',
      lagP95Sec: null,
      lagMaxSec: null,
      sampleCount: 0,
    }),
  );

  // Per-row dynamic ceiling: heights scale to this row's own peak so small
  // variations (e.g. 3s vs 8s) occupy the full bar height rather than being
  // squashed by a fixed 2h ceiling. Severity color stays absolute.
  const clampSec = computeDynamicClampSec(padded);

  // Headline for assistive tech: derived from the same padded series the bars
  // render, so SR/keyboard users get peak p95 + down/warn counts without the
  // mouse-only HoverTooltip.
  const ariaSummary = buildAriaSummary(windowSel, endpointKey, padded);

  const [hover, setHover] = useState<{
    idx: number;
    x: number;
    y: number;
  } | null>(null);
  const hovered = hover ? padded[hover.idx] : null;

  return (
    <>
      <div
        key={windowSel}
        className={clsx('flex items-stretch gap-[2px]', heightClass)}
        role="img"
        aria-label={ariaSummary}
        onMouseOver={(e) => {
          const cell = (e.target as HTMLElement).closest<HTMLElement>(
            '[data-idx]',
          );
          if (!cell) return;
          const idx = Number(cell.dataset['idx']);
          const rect = cell.getBoundingClientRect();
          setHover((prev) =>
            prev && prev.idx === idx
              ? prev
              : { idx, x: rect.left + rect.width / 2, y: rect.top },
          );
        }}
        onMouseLeave={() => {
          setHover(null);
        }}
      >
        {padded.map((b, idx) => {
          const epDown = b.downCount ?? 0;
          const epWarn = b.warnCount ?? 0;
          const failureOverride =
            epDown > 0 ? 'down' : epWarn > 0 ? 'warn' : null;
          const tone = failureOverride ?? lagToTone(b.lagP95Sec, pipeline);
          // Pending = no probes for this slice yet but it's still within the
          // expected fill window (and not already overridden as warn/down).
          const pending =
            !failureOverride &&
            b.startAt !== '' &&
            (b.totalProbes ?? 0) === 0 &&
            b.sampleCount === 0 &&
            (() => {
              const start = new Date(b.startAt).getTime();
              if (!Number.isFinite(start)) return false;
              return renderNow - (start + windowMs) < 2 * windowMs;
            })();
          const heightPct =
            failureOverride !== null || pending
              ? 100
              : lagToHeightPct(b.lagP95Sec, clampSec);
          const key = b.startAt || `lag-${String(idx)}`;
          return (
            <div
              key={key}
              data-idx={idx}
              className="flex min-w-[2px] flex-1 flex-col justify-end overflow-hidden"
            >
              <div
                className={clsx(
                  'w-full rounded-[2.5px]',
                  pending ? 'bar-pending' : TONE_BAR[tone],
                )}
                style={{ height: `${String(heightPct)}%` }}
              />
            </div>
          );
        })}
      </div>
      {hover && hovered && (
        <HoverTooltip x={hover.x} y={hover.y}>
          <LagBucketTip
            bucket={hovered}
            pipeline={pipeline}
            windowSel={windowSel}
          />
        </HoverTooltip>
      )}
    </>
  );
}

export function LagBucketTip({
  bucket,
  pipeline,
  windowSel,
}: {
  bucket: LagBucketView;
  pipeline: Pipeline;
  windowSel: WindowSel;
}) {
  const downCount = bucket.downCount ?? 0;
  const warnCount = bucket.warnCount ?? 0;
  const totalProbes = bucket.totalProbes ?? 0;
  const failureOverride: LagTone | null =
    downCount > 0 ? 'down' : warnCount > 0 ? 'warn' : null;
  const tone: LagTone = failureOverride ?? lagToTone(bucket.lagP95Sec, pipeline);
  const range = formatLagTimeRange(bucket, windowSel);

  if (totalProbes === 0 && bucket.sampleCount === 0) {
    return (
      <>
        <TipHeader tone="none" />
        <TipTime>{range}</TipTime>
      </>
    );
  }

  if (bucket.sampleCount === 0 && (downCount > 0 || warnCount > 0)) {
    return (
      <>
        <TipHeader tone={tone} />
        <TipTime>{range}</TipTime>
        <TipDetail>
          {downCount > 0 ? `${String(downCount)} down` : null}
          {downCount > 0 && warnCount > 0 ? ' · ' : null}
          {warnCount > 0 ? `${String(warnCount)} warn` : null}
          {' · lag not measured'}
        </TipDetail>
      </>
    );
  }

  const p95Sec = bucket.lagP95Sec === null ? '—' : `${String(bucket.lagP95Sec)}s`;
  const maxSec = bucket.lagMaxSec === null ? '—' : `${String(bucket.lagMaxSec)}s`;

  return (
    <>
      <TipHeader tone={tone} />
      <TipTime>{range}</TipTime>
      <TipDetail>
        p95: {p95Sec} · max: {maxSec} · {String(bucket.sampleCount)} samples
      </TipDetail>
      {(downCount > 0 || warnCount > 0) && (
        <TipDetail>
          {String(downCount)} down · {String(warnCount)} warn
        </TipDetail>
      )}
    </>
  );
}

export const LagHistoryBar = memo(LagHistoryBarImpl);
